import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { errorMessage } from '../pb'
import {
  acceptGuidelines,
  authorOf,
  canReply,
  canStartTopic,
  createReply,
  createTopic,
  hasAcceptedGuidelines,
  loadCategories,
  loadReplies,
  loadStats,
  loadTopic,
  loadTopics,
} from '../forum'
import type { ForumCategory, ForumReply, ForumStats, ForumTopic } from '../forum'
import { isMember, parsePocketBaseInstant } from '../session'
import type { CovellaUser } from '../session'
import { Modal } from '../shell/Modal'
import { useToast } from '../shell/Toast'

/**
 * The Keepers' forum, the main column of the Sisters page, laid out the way a message board is:
 * forums in banded tables with topic and post counts and a last-post column, topics in a table,
 * and a thread as a stack of posts with the author's panel on the left.
 *
 * Three screens on real paths: `/sisters` (latest topics first, then the forums),
 * `/sisters/c/:id` (a forum's topics) and `/sisters/t/:id` (a thread and its replies). Reading
 * and posting are Keepers-only; a woman who is not one is told so and nothing is requested.
 */

function useGuidelines(userId: string) {
  const [accepted, setAccepted] = useState<boolean | null>(null)
  useEffect(() => {
    let alive = true
    void hasAcceptedGuidelines(userId).then((value) => alive && setAccepted(value))
    return () => {
      alive = false
    }
  }, [userId])
  return { accepted, setAccepted }
}

export function ForumPanel({ user }: { user: CovellaUser }) {
  const { pathname } = useLocation()
  const parts = pathname.replace(/\/+$/, '').split('/').filter(Boolean)
  const screen = parts[1] === 'c' ? 'category' : parts[1] === 't' ? 'topic' : 'home'
  const id = parts[2] ?? ''

  if (!isMember(user)) {
    return (
      <section className="card forum-locked">
        <h2 className="section-title">The forum is for Keepers.</h2>
        <p className="section-lede">
          Members of the Keepers read and post here. The prayer board beside this page is open to
          every sister.
        </p>
        <div className="hero-cta">
          <Link className="btn-primary" to="/keepers">
            What membership includes
          </Link>
        </div>
      </section>
    )
  }

  return screen === 'topic' ? (
    <TopicScreen user={user} topicId={id} />
  ) : screen === 'category' ? (
    <CategoryScreen user={user} categoryId={id} />
  ) : (
    <ForumHome />
  )
}

/** Categories, topics and per-topic stats, loaded together. */
function useForumData() {
  const [categories, setCategories] = useState<ForumCategory[] | null>(null)
  const [topics, setTopics] = useState<ForumTopic[] | null>(null)
  const [stats, setStats] = useState<ForumStats | null>(null)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    try {
      const [c, t] = await Promise.all([loadCategories(), loadTopics()])
      setCategories(c)
      setTopics(t)
      setStats(await loadStats(t))
      setError(null)
    } catch (err) {
      setError(errorMessage(err, 'Could not load the forum'))
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  return { categories, topics, stats, error, reload }
}

/** "Today, 3:05 PM", "Yesterday, 9:40 AM", then "Sep 29, 2026, 10:00 AM". The way a board dates a post. */
function vbDate(created: string): string {
  const at = parsePocketBaseInstant(created)
  if (!at) return ''
  const time = at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  const start = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const days = Math.round((start(new Date()) - start(at)) / 86400000)
  if (days === 0) return `Today, ${time}`
  if (days === 1) return `Yesterday, ${time}`
  return `${at.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}, ${time}`
}

function Crumbs({ trail }: { trail: { label: string; to?: string }[] }) {
  return (
    <nav className="vb-crumbs" aria-label="Breadcrumb">
      <Link to="/sisters">Sisters</Link>
      {trail.map((step) => (
        <span key={step.label}>
          {' › '}
          {step.to ? <Link to={step.to}>{step.label}</Link> : step.label}
        </span>
      ))}
    </nav>
  )
}

function Band({ title }: { title: string }) {
  return (
    <div className="vb-band">
      <h2>{title}</h2>
    </div>
  )
}

function TopicTable({ topics, stats, title }: { topics: ForumTopic[]; stats: ForumStats; title: string }) {
  return (
    <section className="vb-table">
      <Band title={title} />
      <div className="vb-cols vb-topic-cols">
        <span />
        <span>Topic</span>
        <span className="vb-num">Replies</span>
        <span>Last post</span>
      </div>
      {topics.length === 0 && <p className="vb-none">No topics yet.</p>}
      {topics.map((topic) => {
        const st = stats.byTopic[topic.id]
        return (
          <div className="vb-row vb-topic-cols" key={topic.id}>
            <span className="vb-ico">
              <i
                className={
                  topic.locked
                    ? 'ph-fill ph-lock-simple'
                    : topic.pinned
                      ? 'ph-fill ph-push-pin'
                      : 'ph-fill ph-chat-circle-text'
                }
                aria-hidden="true"
              />
            </span>
            <div>
              <Link className="vb-title" to={`/sisters/t/${topic.id}`}>
                {topic.pinned && <b className="vb-tag">Pinned: </b>}
                {topic.title}
              </Link>
              <div className="vb-meta">
                Started by {authorOf(topic)}
                {topic.expand?.category && <> in {topic.expand.category.name}</>}
              </div>
            </div>
            <span className="vb-num">{st?.replies ?? 0}</span>
            <div className="vb-last">
              <div>{vbDate(st?.lastAt ?? topic.created)}</div>
              <div className="vb-meta">by {st?.lastBy ?? authorOf(topic)}</div>
            </div>
          </div>
        )
      })}
    </section>
  )
}

function ForumRows({
  categories,
  topics,
  stats,
  all,
}: {
  categories: ForumCategory[]
  topics: ForumTopic[]
  stats: ForumStats
  all: ForumCategory[]
}) {
  return (
    <>
      <div className="vb-cols vb-forum-cols">
        <span />
        <span>Forum</span>
        <span className="vb-num">Topics</span>
        <span className="vb-num">Posts</span>
        <span>Last post</span>
      </div>
      {categories.map((category) => {
        const ids = new Set([category.id, ...all.filter((c) => c.parent === category.id).map((c) => c.id)])
        const inside = topics.filter((t) => ids.has(t.category))
        const posts = inside.reduce((sum, t) => sum + 1 + (stats.byTopic[t.id]?.replies ?? 0), 0)
        const last = inside
          .map((t) => ({ t, at: stats.byTopic[t.id]?.lastAt ?? t.created }))
          .sort((x, y) => y.at.localeCompare(x.at))[0]
        return (
          <div className="vb-row vb-forum-cols" key={category.id}>
            <span className="vb-ico">
              <i className={category.read_only ? 'ph-fill ph-archive' : 'ph-fill ph-chats-circle'} aria-hidden="true" />
            </span>
            <div>
              <Link className="vb-title" to={`/sisters/c/${category.id}`}>
                {category.name}
              </Link>
              {category.description && <div className="vb-meta">{category.description}</div>}
            </div>
            <span className="vb-num">{inside.length}</span>
            <span className="vb-num">{posts}</span>
            <div className="vb-last">
              {last ? (
                <>
                  <Link className="vb-lasttitle" to={`/sisters/t/${last.t.id}`}>
                    {last.t.title}
                  </Link>
                  <div className="vb-meta">
                    {vbDate(last.at)} by {stats.byTopic[last.t.id]?.lastBy ?? authorOf(last.t)}
                  </div>
                </>
              ) : (
                <span className="vb-meta">No posts</span>
              )}
            </div>
          </div>
        )
      })}
    </>
  )
}

function ForumHome() {
  const { categories, topics, stats, error } = useForumData()

  if (error) return <p className="notice">{error}</p>
  if (!categories || !topics || !stats) return <p className="quiet-center">One moment</p>

  const top = categories.filter((c) => !c.parent)
  const latest = topics
    .slice()
    .sort((a, b) =>
      (stats.byTopic[b.id]?.lastAt ?? b.created).localeCompare(stats.byTopic[a.id]?.lastAt ?? a.created),
    )
    .slice(0, 8)
  // A category with sub-forums becomes a band of its own; the rest share one band.
  const parents = top.filter((c) => categories.some((x) => x.parent === c.id))
  const loose = top.filter((c) => !categories.some((x) => x.parent === c.id))
  const posts = topics.reduce((sum, t) => sum + 1 + (stats.byTopic[t.id]?.replies ?? 0), 0)

  return (
    <div className="vb">
      <div className="vb-titlebar">
        <div>
          <h1>Sisters</h1>
          <p>The Keepers' forum</p>
        </div>
      </div>

      <TopicTable topics={latest} stats={stats} title="Latest topics" />

      {parents.map((parent) => (
        <section className="vb-table" key={parent.id}>
          <Band title={parent.name} />
          <ForumRows
            categories={categories.filter((c) => c.parent === parent.id)}
            topics={topics}
            stats={stats}
            all={categories}
          />
        </section>
      ))}
      {loose.length > 0 && (
        <section className="vb-table">
          <Band title="Forums" />
          <ForumRows categories={loose} topics={topics} stats={stats} all={categories} />
        </section>
      )}
      {top.length === 0 && <p className="vb-none">No forums have been set up yet.</p>}

      <p className="vb-stats">
        Topics: <b>{topics.length}</b> · Posts: <b>{posts}</b> · Sisters posting:{' '}
        <b>{Object.keys(stats.postsBy).length}</b>
      </p>

    </div>
  )
}

function CategoryScreen({ user, categoryId }: { user: CovellaUser; categoryId: string }) {
  const { categories, topics, stats, error, reload } = useForumData()
  const [composing, setComposing] = useState(false)

  if (error) return <p className="notice">{error}</p>
  if (!categories || !topics || !stats) return <p className="quiet-center">One moment</p>

  const category = categories.find((c) => c.id === categoryId)
  if (!category) {
    return (
      <div className="vb">
        <p className="vb-none">That forum isn't here any more.</p>
        <Link className="btn-outline" to="/sisters">
          Back to Sisters
        </Link>
      </div>
    )
  }
  const parent = categories.find((c) => c.id === category.parent)
  const children = categories.filter((c) => c.parent === category.id)
  const ids = new Set([category.id, ...children.map((c) => c.id)])
  const inside = topics.filter((t) => ids.has(t.category))

  return (
    <div className="vb">
      <Crumbs
        trail={[
          ...(parent ? [{ label: parent.name, to: `/sisters/c/${parent.id}` }] : []),
          { label: category.name },
        ]}
      />
      <div className="vb-titlebar">
        <div>
          <h1>{category.name}</h1>
          {category.description && <p>{category.description}</p>}
        </div>
        {canStartTopic(category, user) ? (
          <button className="btn-primary" type="button" onClick={() => setComposing(true)}>
            New topic
          </button>
        ) : (
          <span className="chip">{category.read_only ? 'Archive' : 'Staff post here'}</span>
        )}
      </div>

      {children.length > 0 && (
        <section className="vb-table">
          <Band title="Sub-forums" />
          <ForumRows categories={children} topics={topics} stats={stats} all={categories} />
        </section>
      )}
      <TopicTable topics={inside} stats={stats} title="Topics" />

      {composing && (
        <NewTopic
          user={user}
          categories={categories}
          initial={category.id}
          onClose={() => setComposing(false)}
          onPosted={() => void reload()}
        />
      )}
    </div>
  )
}

function Post({
  number,
  name,
  posts,
  created,
  body,
  title,
}: {
  number: number
  name: string
  posts: number
  created: string
  body: string
  title?: string
}) {
  return (
    <article className="vb-post">
      <header className="vb-posthead">
        <span>{vbDate(created)}</span>
        <span>#{number}</span>
      </header>
      <div className="vb-postbody">
        <aside className="vb-user">
          <span className="vb-avatar" aria-hidden="true">
            {name.trim().charAt(0).toUpperCase() || 'S'}
          </span>
          <strong>{name}</strong>
          <span className="vb-rank">Keeper</span>
          <span className="vb-meta">Posts: {posts}</span>
        </aside>
        <div className="vb-message">
          {title && <h2 className="vb-msgtitle">{title}</h2>}
          <div className="forum-body">{body}</div>
        </div>
      </div>
    </article>
  )
}

function TopicScreen({ user, topicId }: { user: CovellaUser; topicId: string }) {
  const toast = useToast()
  const { accepted, setAccepted } = useGuidelines(user.id)
  const [topic, setTopic] = useState<ForumTopic | null | undefined>(undefined)
  const [replies, setReplies] = useState<ForumReply[]>([])
  const [postsBy, setPostsBy] = useState<Record<string, number>>({})
  const [category, setCategory] = useState<ForumCategory | undefined>()
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [asking, setAsking] = useState(false)

  const load = useCallback(async () => {
    try {
      const found = await loadTopic(topicId)
      setTopic(found)
      setCategory(found?.expand?.category)
      if (found) {
        setReplies(await loadReplies(found.id))
        // The count beside a name is across the whole forum, so it needs every topic.
        setPostsBy((await loadStats(await loadTopics())).postsBy)
      }
    } catch (err) {
      setTopic(null)
      setError(errorMessage(err, 'Could not load that topic'))
    }
  }, [topicId])

  useEffect(() => {
    void load()
  }, [load])

  async function send() {
    const body = draft.trim()
    if (!body || !topic) return
    setBusy(true)
    setError(null)
    try {
      await createReply(user.id, topic.id, body)
      setDraft('')
      await load()
      toast('Reply posted')
    } catch (err) {
      setError(errorMessage(err, 'Could not post that reply'))
    } finally {
      setBusy(false)
    }
  }

  function submit(event: React.FormEvent) {
    event.preventDefault()
    if (accepted === false) setAsking(true)
    else void send()
  }

  if (topic === undefined) return <p className="quiet-center">One moment</p>
  if (!topic) {
    return (
      <div className="vb">
        <p className="vb-none">{error ?? "That topic isn't here any more."}</p>
        <Link className="btn-outline" to="/sisters">
          Back to Sisters
        </Link>
      </div>
    )
  }

  const open = !topic.locked && canReply(category, user)

  return (
    <div className="vb">
      <Crumbs
        trail={[
          ...(category ? [{ label: category.name, to: `/sisters/c/${category.id}` }] : []),
          { label: topic.title },
        ]}
      />

      <Post
        number={1}
        name={authorOf(topic)}
        posts={postsBy[topic.author] ?? 1}
        created={topic.created}
        body={topic.body}
        title={topic.title}
      />
      {replies.map((reply, index) => (
        <Post
          key={reply.id}
          number={index + 2}
          name={authorOf(reply)}
          posts={postsBy[reply.author] ?? 1}
          created={reply.created}
          body={reply.body}
        />
      ))}

      <section className="vb-table">
        <Band title="Reply to this topic" />
        {open ? (
          <form className="forum-compose vb-compose" onSubmit={submit}>
            <label className="visually-hidden" htmlFor="reply">
              Your reply
            </label>
            <textarea id="reply" rows={5} value={draft} onChange={(e) => setDraft(e.target.value)} required />
            {error && <p className="notice">{error}</p>}
            <button className="btn-primary" type="submit" disabled={busy}>
              {busy ? 'Posting' : 'Post reply'}
            </button>
          </form>
        ) : (
          <p className="vb-none">
            {topic.locked ? 'This topic is locked.' : 'Replies are closed in this forum.'}
          </p>
        )}
      </section>

      {asking && (
        <GuidelinesModal
          userId={user.id}
          onClose={() => setAsking(false)}
          onAccepted={() => {
            setAccepted(true)
            setAsking(false)
            void send()
          }}
        />
      )}
    </div>
  )
}

function NewTopic({
  user,
  categories,
  initial,
  onClose,
  onPosted,
}: {
  user: CovellaUser
  categories: ForumCategory[]
  initial?: string
  onClose: () => void
  onPosted: () => void
}) {
  const navigate = useNavigate()
  const toast = useToast()
  const { accepted, setAccepted } = useGuidelines(user.id)
  const choices = useMemo(() => categories.filter((c) => canStartTopic(c, user)), [categories, user])
  const [category, setCategory] = useState(
    initial && choices.some((c) => c.id === initial) ? initial : (choices[0]?.id ?? ''),
  )
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [asking, setAsking] = useState(false)

  async function send() {
    setBusy(true)
    setError(null)
    try {
      const made = await createTopic(user.id, category, title.trim(), body.trim())
      onPosted()
      onClose()
      toast('Topic posted')
      navigate(`/sisters/t/${made.id}`)
    } catch (err) {
      setError(errorMessage(err, 'Could not post that topic'))
    } finally {
      setBusy(false)
    }
  }

  function submit(event: React.FormEvent) {
    event.preventDefault()
    if (!category || !title.trim() || !body.trim()) return
    if (accepted === false) setAsking(true)
    else void send()
  }

  return (
    <Modal title="New topic" icon="ph-chats" onClose={onClose}>
      {choices.length === 0 ? (
        <p className="forum-empty">No forum is open for new topics right now.</p>
      ) : (
        <form className="stack" onSubmit={submit}>
          <div>
            <label className="field-label" htmlFor="nt-cat">
              Forum
            </label>
            <select id="nt-cat" value={category} onChange={(e) => setCategory(e.target.value)}>
              {choices.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="field-label" htmlFor="nt-title">
              Title
            </label>
            <input id="nt-title" value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>
          <div>
            <label className="field-label" htmlFor="nt-body">
              What's on your mind
            </label>
            <textarea id="nt-body" rows={6} value={body} onChange={(e) => setBody(e.target.value)} required />
          </div>
          {error && <p className="notice">{error}</p>}
          <button className="btn-primary" type="submit" disabled={busy}>
            {busy ? 'Posting' : 'Post topic'}
          </button>
        </form>
      )}
      {asking && (
        <GuidelinesModal
          userId={user.id}
          onClose={() => setAsking(false)}
          onAccepted={() => {
            setAccepted(true)
            setAsking(false)
            void send()
          }}
        />
      )}
    </Modal>
  )
}

/** The forum's one-time guidelines, the same four lines the app shows. */
function GuidelinesModal({
  userId,
  onClose,
  onAccepted,
}: {
  userId: string
  onClose: () => void
  onAccepted: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function agree() {
    setBusy(true)
    setError(null)
    try {
      await acceptGuidelines(userId)
      onAccepted()
    } catch (err) {
      setError(errorMessage(err, 'Could not save that'))
      setBusy(false)
    }
  }

  return (
    <Modal title="Before you post" sub="A shared space for the Keepers here." icon="ph-chats" top onClose={onClose}>
      <ul className="forum-rules">
        <li>Speak as you would to a sister sitting across the table.</li>
        <li>Disagree with kindness. Correct with gentleness.</li>
        <li>What's shared here stays here.</li>
        <li>
          Staff may remove a post or mute an account that breaks these. Repeated or serious problems
          can mean a full ban.
        </li>
      </ul>
      {error && <p className="notice">Couldn't record that: {error}</p>}
      <button className="btn-primary" type="button" disabled={busy} onClick={() => void agree()}>
        {error ? 'Try again' : 'I agree'}
      </button>
    </Modal>
  )
}
